import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getBackendUrl } from '../utils/apiUrl';
import { ADMIN_SESSION_EXPIRED, expireAdminSession } from '../utils/adminSession';

const AuthContext = createContext(null);

// Helper to detect tenant subdomain
const getTenantSubdomainForLogout = () => {
  const hostname = window.location.hostname;
  // Skip localhost and main domain
  if (hostname === 'localhost' || hostname === 'plateos.io' || hostname === 'www.plateos.io') {
    return null;
  }
  // Check for tenant subdomain pattern (e.g., tenant-name.plateos.io)
  const parts = hostname.split('.');
  if (parts.length >= 3 && (parts[1] === 'plateos' || hostname.includes('.emergent.host'))) {
    return parts[0];
  }
  return null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('plateos_user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  const [token, setToken] = useState(() => {
    return localStorage.getItem('plateos_token') || null;
  });
  
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const clearSession = () => { setUser(null); setToken(null); };
    window.addEventListener(ADMIN_SESSION_EXPIRED, clearSession);
    return () => window.removeEventListener(ADMIN_SESSION_EXPIRED, clearSession);
  }, []);

  const login = useCallback(async (email, password) => {
    return new Promise((resolve, reject) => {
      // Determine backend URL based on environment
      const backendUrl = getBackendUrl();
      
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${backendUrl}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          reject(new Error('Server error. Please try again.'));
          return;
        }
        
        if (xhr.status >= 400) {
          reject(new Error(data.detail || 'Invalid email or password'));
          return;
        }
        
        setToken(data.access_token);
        setUser(data.user);
        
        localStorage.setItem('plateos_token', data.access_token);
        localStorage.setItem('plateos_user', JSON.stringify(data.user));
        
        resolve(data.user);
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    // Clear all possible storage keys
    localStorage.removeItem('plateos_token');
    localStorage.removeItem('plateos_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant_id');
    // Defense-in-depth beside the tenant-keyed read: the setup-tour handoff is
    // sessionStorage, which survives the same-tab navigation below — a later
    // login must never inherit another tenant's pending tour.
    try { sessionStorage.removeItem('plateos_show_setup_tour'); } catch { /* ignore */ }

    // Redirect based on context - tenant subdomain vs main site
    const tenantSubdomain = getTenantSubdomainForLogout();
    if (tenantSubdomain) {
      // On tenant subdomain, redirect to admin login (stays on same subdomain)
      window.location.href = '/admin';
    } else {
      // On main site, redirect to super admin login
      window.location.href = '/superadmin/login';
    }
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      expireAdminSession(token);
      throw new Error('Session expired');
    }

    return response;
  }, [token]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    fetchWithAuth,
    isAuthenticated: !!token,
    isSuperAdmin: user?.role === 'super_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
